import { useMemo } from "react";
import type { Itinerary } from "@b2bportal/ground-api";
import type { GroundCancelScenarioV2 } from "@b2bportal/rbc-ground-cancel-v2-api";
import { GroundCancelScenarioV2Enum } from "@b2bportal/rbc-ground-cancel-v2-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useCarReservationCheckoutBreakdown } from "./useCarReservationCheckoutBreakdown";
import { getRefundAmount } from "@/modules/cars-postbooking/components/CarTripCancellationPolicy/getCancelCopy";

export const useCarCancelPolicyBreakdown = (
  cancelScenario: GroundCancelScenarioV2 | undefined,
  carItinerary: Itinerary | undefined
) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const { amountPaid, pointsRedeemed } = useCarReservationCheckoutBreakdown(
    carItinerary ?? null
  );

  const isFullyRefundable =
    cancelScenario?.GroundCancelScenarioV2 ===
      GroundCancelScenarioV2Enum.FullyRefundable ||
    cancelScenario?.GroundCancelScenarioV2 ===
      GroundCancelScenarioV2Enum.RefundableWithConditions;

  const isRefundable = isFullyRefundable;

  // When the backend quotes a refund (e.g. a flexible policy with a penalty),
  // show that instead of assuming the full amount paid comes back.
  const quotedRefund = useMemo(() => {
    const refundAmount = cancelScenario
      ? getRefundAmount(cancelScenario)
      : undefined;
    if (!refundAmount) return null;
    const value = parseFloat(refundAmount.amount);
    if (Number.isNaN(value)) return null;
    return { value, currencyCode: refundAmount.currencyCode };
  }, [cancelScenario]);

  const breakdown = useMemo(() => {
    if (!isRefundable || !carItinerary) return null;

    // paymentBreakdown is scoped to this car by the BFF, also inside a package
    // (hts-rbc-bff #621), so no package detection is needed here (RBC-1988).
    if (!amountPaid) return null;

    const totalPaid = formatFiatCurrency(amountPaid);
    const cashRefund = formatFiatCurrency(quotedRefund ?? amountPaid);

    let pointsPaidLabel: string | undefined;
    let pointsPaid: string | undefined;
    let estimatedRefund: string;

    if (pointsRedeemed) {
      pointsPaidLabel = t("selfServe.pointsPaidAtBooking");
      pointsPaid = formatRewardsCurrency({
        value: pointsRedeemed.value.numberOfPoints,
        currency: "",
      });
      estimatedRefund = t("selfServe.estimatedRefundWithPoints", {
        points: pointsPaid,
        cash: cashRefund,
      });
    } else {
      estimatedRefund = cashRefund;
    }

    return {
      totalPaidLabel: t("selfServe.cars.totalPaidAtBooking"),
      totalPaid,
      pointsPaidLabel,
      pointsPaid,
      estimatedRefund,
    };
  }, [
    isRefundable,
    carItinerary,
    amountPaid,
    quotedRefund,
    pointsRedeemed,
    formatFiatCurrency,
    formatRewardsCurrency,
    t,
  ]);

  return { isRefundable, breakdown };
};
